"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.processAlarms = exports.ActiveAlarm = exports.AlarmId = exports.NAMESPACE = void 0;
const effect_1 = require("effect");
const date_1 = require("../../../date");
const entity_1 = require("../../../entity");
const alarm_1 = require("../capability/alarm");
exports.NAMESPACE = 'Culligan.Device.Class.ActiveAlarm';
/**
 * Checks if two alarms are `deep` equal (i.e. have the same constructor and all `params` if any, are equal).
 */
const areEqual = effect_1.Schema.equivalence(alarm_1.Alarm);
exports.AlarmId = effect_1.Schema.NonEmptyString.annotations({ identifier: `${exports.NAMESPACE}.AlarmId` });
exports.ActiveAlarm = effect_1.Schema.Struct({
    ...entity_1.BaseTimestampedEntity.fields,
    id: exports.AlarmId,
    time: date_1.Timestamp,
    alarm: alarm_1.Alarm,
}).annotations({
    identifier: `${exports.NAMESPACE}.ActiveAlarm`,
    description: 'Item from the history of alarms occurred on a device',
});
/**
 * Processes the list of active alarms based on:
 * - newly detected (implicit) alarms,
 * - dismissible alarm types,
 * - the current report time,
 * - and the current time.
 *
 * Handles deduplication, automatic dismissal, and addition or update of alarms.
 *
 * @param params - The parameters object.
 * @param params.active - The current list of active alarms.
 * @param params.implicit - Alarms implicitly generated from the device's current state.
 * @param params.dismissable - Alarm types that support automatic dismissal (e.g., telemetry-based alarms).
 * @param params.reportDate - The timestamp from the current device report.
 * @param params.now - The current time, used to evaluate alarm freshness or expiration.
 * @param params.makeId - Function to generate unique IDs for new alarms.
 *
 * @returns The updated list of active alarms.
 */
const processAlarms = ({ active, implicit, dismissable, reportDate, now, makeId, }) => {
    const operations = {
        created: [],
        updated: [],
        dismissed: [],
    };
    const dedupedActiveAlarms = effect_1.Array.dedupeWith(active, (a, b) => areEqual(a.alarm, b.alarm));
    const dismissed = dedupedActiveAlarms.filter(activeAlarm => {
        const isDismissable = dismissable.some(d => d._tag === activeAlarm.alarm._tag);
        const isNotInImplicit = !implicit.some(n => n.is(activeAlarm.alarm));
        return isDismissable && isNotInImplicit;
    });
    operations.dismissed = dismissed;
    for (const newAlarm of implicit) {
        const alreadyActiveAlarm = dedupedActiveAlarms.find(({ alarm }) => alarm.is(newAlarm));
        if (!alreadyActiveAlarm) {
            operations.created.push(exports.ActiveAlarm.make({
                id: makeId(),
                createdAt: now,
                updatedAt: now,
                time: reportDate,
                alarm: newAlarm,
            }));
        }
        else if (!areEqual(alreadyActiveAlarm.alarm, newAlarm)) {
            operations.updated.push(exports.ActiveAlarm.make({
                ...alreadyActiveAlarm,
                time: reportDate,
                updatedAt: now,
                alarm: newAlarm,
            }));
        }
    }
    const remainingAlarms = dedupedActiveAlarms.filter(alarm => !operations.dismissed.some(dismissed => dismissed.id === alarm.id));
    const updatedAlarms = remainingAlarms.map(alarm => operations.updated.find(updated => updated.id === alarm.id) || alarm);
    const newActiveAlarms = [...operations.created, ...updatedAlarms].sort((a, b) => b.time.getTime() - a.time.getTime());
    return {
        ...operations,
        active: newActiveAlarms,
    };
};
exports.processAlarms = processAlarms;
//# sourceMappingURL=active-alarm.js.map